import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { testAPI } from '../../utils/api';
import { DashboardLayout } from '../../components/layout';
import { Spinner } from '../../components/ui';
import toast from 'react-hot-toast';

export default function TestHistory() {
  const nav = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('all');

  useEffect(() => {
    testAPI.getHistory()
      .then(r => setHistory(r.data.history || r.data.results || []))
      .catch(() => toast.error('Failed to load history'))
      .finally(() => setLoading(false));
  }, []);

  const avg = history.length
    ? (history.reduce((s,r) => s + parseFloat(r.percentage||0), 0) / history.length).toFixed(1)
    : 0;
  const best = history.length
    ? Math.max(...history.map(r => parseFloat(r.percentage||0))).toFixed(1)
    : 0;
  const totalCorrect = history.reduce((s,r) => s + (r.correct||0), 0);

  const filtered = history.filter(r => {
    if (filter === 'pass')  return parseFloat(r.percentage) >= 60;
    if (filter === 'fail')  return parseFloat(r.percentage) < 60;
    return true;
  });

  if (loading) return (
    <DashboardLayout title="Test History">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:400 }}>
        <Spinner size={40} color="blue" />
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout title="Test History 📊" subtitle="All your batch test attempts">

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:14, marginBottom:20 }}>
        {[
          { label:'Tests Taken', value: history.length, icon:'📝', color:'var(--blue)' },
          { label:'Avg Score',   value: avg+'%',         icon:'🎯', color:'#F5A623' },
          { label:'Best Score',  value: best+'%',        icon:'🌟', color:'#28A745' },
          { label:'Total Correct', value: totalCorrect,  icon:'✅', color:'#8B5CF6' },
        ].map(s => (
          <div key={s.label} style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:14, padding:'16px 18px' }}>
            <div style={{ fontSize:22, marginBottom:5 }}>{s.icon}</div>
            <div style={{ fontFamily:'var(--font-heading)', fontWeight:900, fontSize:24, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:12, color:'var(--muted)', marginTop:2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        {[['all','All'],['pass','✅ Pass (≥60%)'],['fail','❌ Below 60%']].map(([k,l]) => (
          <button key={k} onClick={() => setFilter(k)}
            style={{
              padding:'7px 16px', borderRadius:99, fontSize:12, fontWeight:600,
              border:'none', cursor:'pointer',
              background: filter===k ? 'var(--blue)' : 'var(--border)',
              color: filter===k ? '#fff' : 'var(--text)',
            }}>
            {l}
          </button>
        ))}
      </div>

      {/* Results list */}
      {filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 20px' }}>
          <div style={{ fontSize:60, marginBottom:12 }}>📭</div>
          <p style={{ color:'var(--muted)', fontSize:15 }}>No test results found.</p>
          <button className="btn btn-primary" style={{ marginTop:16 }} onClick={() => nav('/dashboard/tests')}>
            Start a Batch →
          </button>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {filtered.map(r => {
            const pct = parseFloat(r.percentage || 0);
            const passed = pct >= 60;
            return (
              <div key={r.id} style={{
                background:'#fff', border:'1px solid var(--border)', borderRadius:14,
                padding:'16px 18px', display:'flex', alignItems:'center', gap:14,
                borderLeft:`4px solid ${passed ? '#28A745' : pct >= 40 ? '#F5A623' : '#dc3545'}`,
              }}>
                {/* Batch number */}
                <div style={{
                  width:44, height:44, borderRadius:'50%', flexShrink:0,
                  background:`linear-gradient(135deg,${passed?'#28A745,#20c997':'#dc3545,#e85d73'})`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  color:'#fff', fontFamily:'var(--font-heading)', fontWeight:900, fontSize:15,
                }}>
                  {r.batch_number}
                </div>

                {/* Info */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:14, marginBottom:3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {r.batch_title || `Batch ${r.batch_number}`}
                  </div>
                  <div style={{ fontSize:12, color:'var(--muted)', display:'flex', gap:12, flexWrap:'wrap' }}>
                    <span>📅 {new Date(r.completed_at).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}</span>
                    <span>✅ {r.correct}/{r.total_questions} correct</span>
                    <span>❌ {r.wrong} wrong</span>
                    <span>⏭ {r.skipped} skipped</span>
                    <span>⏱ {r.time_taken_minutes}m</span>
                  </div>
                </div>

                {/* Score */}
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  <div style={{
                    fontFamily:'var(--font-heading)', fontWeight:900, fontSize:22,
                    color: passed ? '#28A745' : pct>=40 ? '#F5A623' : '#dc3545',
                  }}>
                    {pct.toFixed(1)}%
                  </div>
                  <div style={{ fontSize:11, color: passed?'#28A745':'#dc3545', fontWeight:700 }}>
                    {passed ? '✅ Pass' : '❌ Fail'}
                  </div>
                </div>

                {/* Review btn */}
                <button
                  onClick={() => nav(`/dashboard/batch/${r.batch_id}/result/${r.id}`)}
                  style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8, padding:'6px 12px', cursor:'pointer', fontSize:12, fontWeight:600, flexShrink:0 }}>
                  Review
                </button>
              </div>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
}
